import { useState, useEffect } from 'react';
import { GameVariant, TimeControlPreset } from '@draughts/shared';
import type { ChallengeSettings } from '@draughts/shared';
import LandingSections from './LandingSections';
import { ERROR_DISMISS_MS } from '../../constants/timing';
import { trackChallengeSent, trackGuestPlayClicked, trackGuestOverlayConfirmed, trackGuestOverlayDismissed } from '../../utils/analytics';
import { log } from '../../utils/logger';
import DashboardPlayAIModal from '../dashboard/DashboardPlayAIModal';
import type { AIGameOptions } from '../dashboard/DashboardPlayAIModal';
import PlayOverlay from '../lobby/PlayOverlay';
import CustomTableHost from '../lobby/CustomTableHost';
import { useAuthStore } from '../../store/auth-store';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import './HomePage.css';

interface HomePageProps {
  isAuthenticated: boolean;
  isGuest?: boolean;
  /** Only the name is read here; the rating was never rendered on this page. */
  user: { username: string } | null;
  onPlayAI: (variant: GameVariant, difficulty: 'very-easy' | 'easy' | 'medium' | 'hard' | 'expert' | 'master', timeControl: TimeControlPreset, options?: AIGameOptions) => void;
  /** `null` se la sfida e' partita, altrimenti la CHIAVE i18n del motivo del no. */
  onChallengePlayer?: (userId: string, settings: ChallengeSettings) => Promise<string | null>;
  /** Create a shareable challenge link (for a friend who is not in the app). */
  onCreateChallengeLink?: (settings: ChallengeSettings) => Promise<void>;
  /** Watch the live game of a player who is busy playing. */
  onSpectatePlayer?: (userId: string) => void;
  /** Avvia una partita in presenza (due persone, questo dispositivo). */
  onPlayInPerson?: (settings: ChallengeSettings) => void;
  onLoginRequired: () => void;
  onCreateAccount?: () => void;
  /**
   * Entra come ospite. Restituisce la chiave i18n dell'errore, o `null` se è
   * andata: il fallimento va detto, non ingoiato (vedi `handlePlayAsGuest`).
   */
  onPlayAsGuest?: () => Promise<string | null>;
  onNavigateToProfile?: () => void;
  /**
   * Porta alla lista dei tornei, la stessa della barra e della hero della
   * dashboard. Un tempo era la pagina-lista delle ARENE, e chi cercava un
   * torneo non lo trovava; poi un foglio con dentro le due liste, che era
   * un'anticamera; ora e' la cosa stessa, e in cima ha il tasto per aprire un
   * torneo proprio.
   */
  onNavigateToTournament?: (id: string) => void;
  onSolvePuzzle?: (puzzleId: number, practice?: boolean) => void;
  /** Open the fully client-side landing demo puzzle (solvable without auth). */
  onOpenDemoPuzzle?: () => void;
  /** Open the rules page for a specific variant (from the variants showcase). */
  onOpenVariantRules?: (variant: GameVariant) => void;
  onOpenGameRules?: () => void;
  /** Apre le tabelle di apertura dentro l'app (dal foglio Gioca e da Sfida diretta). */
  onOpenOpeningTables?: (variant: string) => void;
  /** When set (from a rules-page "Play" CTA), auto-open the play modal preset to this variant/mode. */
  initialPlay?: { variant: GameVariant; mode: 'ai' | 'online' } | null;
  /** Called once initialPlay has been consumed, so the parent can clear it. */
  onInitialPlayConsumed?: () => void;
}

export default function HomePage({
  isAuthenticated,
  isGuest,
  onPlayAI,
  onChallengePlayer,
  onCreateChallengeLink,
  onSpectatePlayer,
  onPlayInPerson,
  onLoginRequired,
  onCreateAccount,
  onPlayAsGuest,
  onNavigateToProfile,
  onNavigateToTournament,
  onSolvePuzzle,
  onOpenDemoPuzzle,
  onOpenVariantRules,
  onOpenGameRules,
  onOpenOpeningTables,
  initialPlay,
  onInitialPlayConsumed,
}: HomePageProps) {


  // Game options modal state. The modal UI itself is the shared dashboard
  // component (DashboardPlayAIModal / PlayOverlay); here we only
  // track which one is open and the preset variant/time control to open with.
  const [showOptions, setShowOptions] = useState(false);
  const [optionsMode, setOptionsMode] = useState<'ai' | 'online' | 'challenge' | null>(null);

  // Challenge player state

  // Preset variant / time control the play modal opens with (set by the
  // rules-page "Play" CTA via initialPlay, and by the guest/online handlers).
  const [variant, setVariant] = useState<GameVariant>(GameVariant.INTERNATIONAL);
  const [timeControl, setTimeControl] = useState<TimeControlPreset>('blitz-5+3');

  // Pending action: open options modal after auth/guest login
  const [pendingPlayOnline, setPendingPlayOnline] = useState(false);
  /** Chiave i18n dell'errore dell'accesso ospite, mostrata sotto la CTA. */
  const [erroreOspite, setErroreOspite] = useState<string | null>(null);

  // When user becomes authenticated while pendingPlayOnline, open options modal
  useEffect(() => {
    if (isAuthenticated && pendingPlayOnline) {
      setPendingPlayOnline(false);
      setOptionsMode('online');
      setShowOptions(true);
    }
  }, [isAuthenticated, pendingPlayOnline]);

  /*
   * L'accesso e' stato DECISO, non solo tentato: `accessToken` non e'
   * persistito, quindi al montaggio un utente registrato passa da una finestra
   * in cui `isAuthenticated` e' ancora falso mentre `checkAuth()` e' in volo.
   * Dedurre «e' un ospite» da li' e chiamare `loginAsGuest()` gli riscrive
   * sopra il cookie di sessione. La regola sta scritta in `App.tsx`.
   */
  const isAuthReady = useAuthStore((state) => state.isAuthReady);

  // Auto-open the play modal when arriving from a rules-page "Play" CTA, preset
  // to the requested variant/mode. Online for an unauthenticated visitor goes
  // through the guest-login flow (no register wall); the effect above reopens the
  // online modal once the guest session is established.
  useEffect(() => {
    if (!initialPlay) return;
    setVariant(initialPlay.variant);
    if (initialPlay.mode === 'ai') {
      setOptionsMode('ai');
      setShowOptions(true);
    } else if (isAuthenticated) {
      setTimeControl('blitz-5+3');
      setOptionsMode('online');
      setShowOptions(true);
    } else if (isAuthReady) {
      /*
       * Solo ad accesso DECISO. Questo ramo parte da un link profondo, senza
       * nessun tocco: con la rete lenta un utente registrato ci finiva dentro
       * mentre `checkAuth()` era ancora in volo, e `loginAsGuest()` gli
       * riscriveva sopra il cookie di sessione. Vedi la regola dichiarata in
       * `App.tsx`: si guarda `isAuthReady`, mai `isAuthenticated` da solo.
       */
      setPendingPlayOnline(true);
      setErroreOspite(null);
      // Stesso trattamento del percorso a tocco: se l'ingresso da ospite non
      // riesce, si smette di aspettare e lo si dice.
      void onPlayAsGuest?.().then((chiaveErrore) => {
        if (!chiaveErrore) return;
        setPendingPlayOnline(false);
        setErroreOspite(chiaveErrore);
      });
    } else {
      /* Non si sa ancora chi siamo: si riproverà quando si saprà. */
      return;
    }
    onInitialPlayConsumed?.();
    /*
     * `isAuthReady` e `isAuthenticated` sono in elenco perché il ramo online
     * ora può NON consumare `initialPlay` (accesso ancora indeciso): senza
     * queste due, l'effetto non tornerebbe mai a girare e il link profondo
     * resterebbe caduto nel vuoto. `initialPlay` resta la guardia: consumato,
     * diventa nullo e l'effetto esce alla prima riga.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlay, isAuthReady, isAuthenticated]);

  // Confirm handler for the shared AI modal.
  const handleConfirmAI = (v: string, difficulty: 'very-easy' | 'easy' | 'medium' | 'hard' | 'expert' | 'master', options: AIGameOptions) => {
    log.info('HomePage', 'Starting AI game', { variant: v, difficulty, options });
    // Canonical `game-started` is fired centrally in App.handlePlayAI (covers
    // Home overlay + Dashboard modal + rules CTA with no double-counting).
    onPlayAI(v as GameVariant, difficulty, 'none', options);
    setShowOptions(false);
    setOptionsMode(null);
  };

  // Handle back / close in options
  const handleBack = () => {
    log.info('HomePage', 'Onboarding overlay closed');
    if (optionsMode === 'online') {
      trackGuestOverlayDismissed();
    }
    setShowOptions(false);
    setOptionsMode(null);
  };

  // Handle challenge player mode — opens the custom-game sheet on Sfida diretta
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const handleChallengeClick = () => {
    if (isAuthenticated) {
      if (isGuest) return;
      log.info('HomePage', 'Onboarding overlay opened', { mode: 'challenge' });
      setShowChallengeModal(true);
    } else {
      onLoginRequired();
    }
  };

  /*
    * Il foglio si chiude da se' DOPO un invio riuscito: l'attesa della risposta la
    * mostra `GlobalOutgoingChallengeOverlay`, montato in App. Se invece e' un no,
    * resta aperto e dice il motivo — percio' la chiave torna indietro.
    *
    * ⚠️ `trackChallengeSent` solo quando e' partita davvero. Da quando il rifiuto
    * torna come VALORE invece che come eccezione, un `.then()` senza guardia
    * conterebbe fra le sfide mandate anche quelle che il server ha respinto.
    */
  const handleSendChallenge = async (userId: string, settings: ChallengeSettings): Promise<string | null> => {
    if (!onChallengePlayer) return null;
    try {
      const chiaveErrore = await onChallengePlayer(userId, settings);
      if (!chiaveErrore) {
        trackChallengeSent(settings.variant, settings.timeControl, {
          ruleMode: settings.gameMode,
          opening: settings.opening,
          rated: settings.rated,
        });
      }
      return chiaveErrore;
    } catch (error) {
      log.error('HomePage', 'Failed to send challenge', error);
      return 'online.errors.generico';
    }
  };

  // Handle cancel challenge modal
  const handleCancelChallenge = () => {
    setShowChallengeModal(false);
  };

  // Log when the play modal opens (Esc / back-press / focus handling now live
  // inside the shared dashboard modal components).
  useEffect(() => {
    if (showOptions) log.info('HomePage', 'Onboarding overlay opened', { mode: optionsMode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions]);

  // Get the full user object from the auth store for Dashboard
  const fullUser = useAuthStore((state) => state.user);
  const isAuthLoading = useAuthStore((state) => state.isLoading);

  // Handle "Play as Guest" CTA: auto-login guest if not authenticated, then show overlay
  const handlePlayAsGuestClick = () => {
    log.info('HomePage', 'Play as Guest CTA clicked');
    trackGuestPlayClicked('hero');
    if (isAuthenticated) {
      // Already logged in (guest or registered) → skip login, show overlay
      setTimeControl('blitz-5+3');
      setOptionsMode('online');
      setShowOptions(true);
      return;
    }
    // Guard: don't double-trigger while login is in progress
    if (isAuthLoading) return;
    /* E nemmeno prima che si sappia chi siamo: `isAuthLoading` copre il
       tentativo in corso, `isAuthReady` copre anche il caso in cui l'accesso
       non e' ancora nemmeno cominciato. Vedi `App.tsx`. */
    if (!isAuthReady) return;
    // Set pending so overlay opens once isAuthenticated becomes true
    setPendingPlayOnline(true);
    setErroreOspite(null);
    void onPlayAsGuest?.().then((chiaveErrore) => {
      if (!chiaveErrore) return;
      /*
       * L'accesso ospite è fallito. Senza queste due righe il bottone tornava
       * cliccabile e non compariva niente: `pendingPlayOnline` si azzera solo
       * quando `isAuthenticated` diventa vero, cioè mai in questo caso.
       */
      setPendingPlayOnline(false);
      setErroreOspite(chiaveErrore);
    });
  };

  // Landing demo "Risolvi": the demo puzzle (demo_int) is validated entirely
  // in-browser, so EVERY visitor — logged in or not — can solve it, no auth. The
  // real daily puzzle stays registration-gated (Dashboard only).
  const handleSolveDemoPuzzle = () => {
    log.info('HomePage', 'Demo puzzle Solve clicked', { isAuthenticated });
    onOpenDemoPuzzle?.();
  };

  return (
    <div className="home-page-new">
      {/* Checkerboard pattern background */}
      <div className="home-bg-pattern" />

      {/* Visitors and guests only. A registered user never gets here: App routes
          `isAuthenticated && !isGuest` straight to DashboardPage. */}
      <LandingSections
        onPlayNow={handlePlayAsGuestClick}
        onSolveDemoPuzzle={handleSolveDemoPuzzle}
        onOpenVariantRules={onOpenVariantRules}
        onOpenGameRules={onOpenGameRules}
        playPending={pendingPlayOnline && isAuthLoading}
        guestErrorKey={erroreOspite}
      />

      {/* Play modals — the shared dashboard components, so the guest and
          vs-Computer flows look and behave exactly like the dashboard. */}
      <DashboardPlayAIModal
        isOpen={showOptions && optionsMode === 'ai'}
        onClose={handleBack}
        onConfirm={handleConfirmAI}
        defaultVariant={variant}
      />
      <PlayOverlay
        isOpen={showOptions && optionsMode === 'online'}
        onClose={handleBack}
        onPlayInPerson={onPlayInPerson}
        onPlayAI={(variant, difficulty, options) => onPlayAI(variant, difficulty, 'none', options)}
        onSendChallenge={onChallengePlayer && handleSendChallenge}
        onCreateChallengeLink={onCreateChallengeLink && ((settings) => { void onCreateChallengeLink(settings); })}
        onOpenOpeningTables={onOpenOpeningTables}
        onSignIn={onLoginRequired}
      />

      {/* Lo STESSO foglio della partita personalizzata, aperto sulla Sfida diretta:
          la riga «Avversario» resta, così cambiare destinazione non costa un giro
          da capo. */}
      <CustomTableHost
        isOpen={showChallengeModal}
        onClose={handleCancelChallenge}
        defaultOpponent="challenge"
        defaultVariant={variant}
        rememberSettings
        onOpenOpeningTables={onOpenOpeningTables}
        onPlayInPerson={onPlayInPerson}
        onPlayAI={(v, difficulty, options) => onPlayAI(v, difficulty, 'none', options)}
        onSendChallenge={onChallengePlayer && handleSendChallenge}
        onCreateChallengeLink={onCreateChallengeLink && ((settings) => {
          // Come sopra: il link lo mostra `GlobalChallengeLinkOverlay`.
          void onCreateChallengeLink(settings)
            .catch((error) => log.error('HomePage', 'Failed to create challenge link', error));
        })}
        onSpectate={onSpectatePlayer && ((user) => {
          // Close the sheet FIRST and let the parent navigate on a later tick:
          // it owns a history "buffer" entry that it pops on close, and pushing a
          // new screen in the same tick would race that pop.
          setShowChallengeModal(false);
          onSpectatePlayer(user.userId);
        })}
      />

    </div>
  );
}
